/**
 * Notifications Store (Pinia) — campanella in-app (R2)
 *
 * @version 2.0.0
 * @description Notifiche `database` di Laravel (motore eventi F1+, workflow commesse).
 *              v2.0.0 (notifiche F4): realtime via Reverb — canale privato
 *              `App.Models.User.{id}` (`.notification()`, vedi
 *              useWebsocketStore.listenToUserChannel): la nuova notifica entra
 *              in lista, aggiorna il badge e mostra un toast. Il poll del
 *              contatore resta come fallback (60s senza realtime, 5 min con).
 *              Badging API (`navigator.setAppBadge`) allineata al contatore.
 */

import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import axios from 'axios'
import { standardizeErrorMessage } from '@/utils/utils'
import { useSnackbar } from '@/composables/useSnackbar'
import type { AppNotification } from '@/models/commesse'

const POLL_INTERVAL_MS = 60_000
const POLL_INTERVAL_REALTIME_MS = 5 * 60_000

export const useNotificationsStore = defineStore('notifications', () => {
  const baseUrl = import.meta.env.VITE_API_URL as string

  const loading = ref(false)
  const notifications = ref<AppNotification[]>([])
  const unreadCount = ref(0)
  /** true quando il canale privato Reverb è attivo (poll rallentato a fallback) */
  const realtimeActive = ref(false)
  /** Ultima notifica arrivata in realtime (per chi vuole reagire, es. refetch liste) */
  const lastIncoming = ref<AppNotification | null>(null)
  /** Dialog a tutta pagina su mobile (montata in App.vue, aperta dalla campanella del drawer). */
  const mobileDialogOpen = ref(false)

  /** Apre la dialog mobile e rinfresca la lista (il contenuto resta montato tra un'apertura e l'altra). */
  function openMobileDialog(): void {
    mobileDialogOpen.value = true
    fetchNotifications().catch(() => {})
  }

  let pollTimer: ReturnType<typeof setInterval> | null = null

  async function fetchNotifications(): Promise<AppNotification[]> {
    loading.value = true
    try {
      const { data } = await axios.get(`${baseUrl}/notifications`, {
        params: { itemsPerPage: 30 },
      })
      notifications.value = data.data ?? []
      return notifications.value
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  async function fetchUnreadCount(): Promise<number> {
    try {
      const { data } = await axios.get(`${baseUrl}/notifications/unread-count`)
      unreadCount.value = data.count ?? 0
      return unreadCount.value
    } catch {
      return unreadCount.value // silenzioso: il badge non deve mai rompere la toolbar
    }
  }

  async function markRead(id: string): Promise<void> {
    try {
      await axios.post(`${baseUrl}/notifications/${id}/read`)
      const item = notifications.value.find((n) => n.id === id)
      if (item && item.read_at == null) {
        item.read_at = new Date().toISOString()
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      }
    } catch (error) {
      throw standardizeErrorMessage(error)
    }
  }

  async function markAllRead(): Promise<void> {
    try {
      await axios.post(`${baseUrl}/notifications/read-all`)
      notifications.value.forEach((n) => {
        if (n.read_at == null) n.read_at = new Date().toISOString()
      })
      unreadCount.value = 0
    } catch (error) {
      throw standardizeErrorMessage(error)
    }
  }

  /** Avvia il poll del contatore (idempotente — chiamato dalla campanella). */
  function startPolling(): void {
    if (pollTimer != null) return
    fetchUnreadCount()
    pollTimer = setInterval(fetchUnreadCount, realtimeActive.value ? POLL_INTERVAL_REALTIME_MS : POLL_INTERVAL_MS)
  }

  // ---------------------------------------------------------------------------
  // Realtime (F4)
  // ---------------------------------------------------------------------------

  /** Chiamato da useWebsocketStore quando il canale utente è (dis)attivo. */
  function setRealtime(active: boolean): void {
    if (realtimeActive.value === active) return
    realtimeActive.value = active
    if (pollTimer != null) {
      stopPolling()
      startPolling()
    }
  }

  /**
   * Payload `BroadcastNotificationCreated` (EventNotification::toBroadcast =
   * toArray() + id) → voce della campanella. Idempotente sull'id.
   */
  function handleIncoming(payload: Record<string, any>): void {
    const id = String(payload?.id ?? '')
    if (!id || notifications.value.some((n) => n.id === id)) return
    const { id: _omit, type: _type, ...data } = payload
    const item: AppNotification = {
      id,
      data,
      read_at: null,
      created_at: new Date().toISOString(),
    }
    notifications.value = [item, ...notifications.value].slice(0, 50)
    unreadCount.value += 1
    lastIncoming.value = item
    // E6: una checklist affidata/revocata cambia il conteggio che accende la
    // voce di menu CHECKLIST — import lazy per evitare dipendenze circolari
    if (typeof data.url === 'string' && data.url.startsWith('/checklists/')) {
      import('@/stores/useChecklistsStore').then(({ useChecklistsStore }) => {
        useChecklistsStore().fetchOpenCount().catch(() => {})
      }).catch(() => {})
    }
    try {
      const { showInfo } = useSnackbar()
      showInfo(String(data.title ?? 'Nuova notifica'))
    } catch {
      // snackbar non disponibile (es. fuori dal layout): silenzioso
    }
  }

  // Badging API (PWA installata): badge sull'icona = non letti
  watch(unreadCount, (n) => {
    try {
      const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> }
      if (n > 0 && typeof nav.setAppBadge === 'function') void nav.setAppBadge(n)
      else if (n === 0 && typeof nav.clearAppBadge === 'function') void nav.clearAppBadge()
    } catch {
      // API assente o bloccata
    }
  })

  function stopPolling(): void {
    if (pollTimer != null) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  return {
    loading,
    notifications,
    unreadCount,
    mobileDialogOpen,
    openMobileDialog,
    fetchNotifications,
    fetchUnreadCount,
    markRead,
    markAllRead,
    startPolling,
    stopPolling,
    realtimeActive,
    lastIncoming,
    setRealtime,
    handleIncoming,
  }
})
